import { TranslateService } from '@ngx-translate/core';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { of, Subject } from 'rxjs';
import { switchMap, take, takeUntil } from 'rxjs/operators';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { UserService } from '../../service/user.abstract.service';
import { FESubscription, PaymentType, SpreedlyPaymentMethodResponse } from '../../user.model';
import { ActivatedRoute } from '@angular/router';
import { ITaxResponse, ItemSubscriptionPayload, OrgSubscription, PaymentMethodType, PaymentPayload, TransactionType } from '@bciti/types';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { organisationSelector } from 'src/app/global/store/global.selectors';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';
import { userRecordIdSelector } from '../../store/user.selectors';

@Component({
    selector: 'app-user-subscription-payment-dialog',
    templateUrl: './user-subscription-payment-dialog.component.html',
    styleUrls: ['./user-subscription-payment-dialog.component.scss'],
})
export class SubscriptionPaymentDialogComponent implements OnInit, OnDestroy {
    destroy$ = new Subject();

    Spreedly = (window as any).Spreedly;

    processingSubmit = false;
    showErrors = false;

    errors = [];

    public form: any;
    public inPersonForm: FormGroup;

    public userId: string;

    public taxes: Partial<ITaxResponse>[] = [];
    public taxesTotal: number;
    public itemsSubtotal: number;
    public orgSubscriptions: Partial<OrgSubscription[]>;
    private totalTaxRate = 0;
    public PaymentType = PaymentType;

    public paymentOptions = [
        {
            name: this.translate.instant(`pages.user-subscriptions.payment-type.${PaymentMethodType.CREDIT_CARD}`),
            id: 1,
            val: PaymentMethodType.CREDIT_CARD,
        },
        {
            name: this.translate.instant(`pages.user-subscriptions.payment-type.${PaymentMethodType.COUNTER}`),
            id: 2,
            val: PaymentMethodType.COUNTER,
        },
    ];

    private _selectedPaymentType = this.paymentOptions[0];

    get selectedPaymentType() {
        return this._selectedPaymentType;
    }
    set selectedPaymentType(v) {
        this._selectedPaymentType = v;
    }

    get disableSubmit() {
        switch (this.selectedPaymentType.val) {
            case PaymentMethodType.CREDIT_CARD:
                return this.isFree ? false : this.form.invalid;
            case PaymentMethodType.COUNTER:
                return this.inPersonForm.invalid;
            default:
                console.error('Incorrect payment type');
                return;
        }
    }

    constructor(
        private route: ActivatedRoute,
        private formBuilder: FormBuilder,
        private translate: TranslateService,
        private service: UserService,
        private dialogRef: MatDialogRef<SubscriptionPaymentDialogComponent>,
        private messageService: MessageService,
        private store: Store<State>,
        @Inject(MAT_DIALOG_DATA) public data: FESubscription[],
    ) {}

    ngOnInit() {
        this.form = this.formBuilder.group({
            fullName: new FormControl('', [Validators.required]),
            month: new FormControl('', [Validators.required]),
            year: new FormControl('', [Validators.required]),
        });

        this.inPersonForm = this.formBuilder.group({
            reference: new FormControl('', [Validators.required]),
        });

        this.store
            .select(userRecordIdSelector)
            .pipe(takeUntil(this.destroy$))
            .subscribe((id) => (this.userId = id));

        this.store
            .select(organisationSelector)
            .pipe(takeUntil(this.destroy$))
            .subscribe((org) => {
                this.taxes = org.taxes;
                this.calculateCart();
                this.orgSubscriptions = org.subscriptions;
            });

        this.Spreedly.on('ready', () => {
            document.getElementById('submit-button').removeAttribute('disabled');
            this.Spreedly.setStyle('number', 'width: calc(100% - 22px); height: 2rem; padding: 0 11px;');
            this.Spreedly.setStyle('cvv', 'width: calc(100% - 22px); height: 2rem; padding: 0 11px;');
        });

        this.Spreedly.on('fieldEvent', (name, type, activeEl, inputProperties) => {
            if (type === 'blur') {
                // this.disableSubmit = false;
                // this.clearErrors();
            }
        });
    }

    ngAfterContentInit() {
        if (!this.isFree) {
            setTimeout(() => {
                this.Spreedly.init('66QFw4VIfiTsIjoxZ5xdQTlezcR', {
                    numberEl: 'spreedly-number',
                    cvvEl: 'spreedly-cvv',
                });
            }, 500);
        }
    }

    private calculateCart() {
        this.totalTaxRate = this.taxes.reduce((totalTaxes, tax) => totalTaxes + tax.rate, 0);

        let taxableItemsSubTotal = 0;
        let nonTaxableItemsTotal = 0;
        let taxableItemsTaxTotal = 0;
        let nonTaxableItemsSubTotal = 0;
        let nonTaxableItemsTaxTotal = 0;

        // Split items by taxable
        for (const item of this.data) {
            if (item.taxable) {
                taxableItemsSubTotal += item.cost;
            } else {
                nonTaxableItemsTotal += item.cost;
            }
        }

        if (taxableItemsSubTotal > 0) {
            // First calculate the taxes that will be paid on taxable items
            taxableItemsTaxTotal = taxableItemsSubTotal * this.totalTaxRate;
        }

        if (nonTaxableItemsTotal > 0) {
            // Second calculate the subtotal of the items that are non-taxable
            nonTaxableItemsSubTotal = nonTaxableItemsTotal / (1 + this.totalTaxRate);
            nonTaxableItemsTaxTotal = nonTaxableItemsTotal - nonTaxableItemsSubTotal;
        }

        // Then calculate the pre-tax total and the total taxes for UI
        this.itemsSubtotal = taxableItemsSubTotal + nonTaxableItemsSubTotal;
        this.taxesTotal = +taxableItemsTaxTotal + nonTaxableItemsTaxTotal;
    }

    private clearErrors() {
        this.showErrors = false;
        this.errors = [];
    }

    public getSingleTaxAmount(rate: number): number {
        return this.itemsSubtotal * rate;
    }

    public getTotalTaxAmount(price: number): number {
        let total = 0;
        this.taxes.forEach((tax) => (total += price * tax.rate));
        return total;
    }

    private preparePayload(subscriptions: FESubscription[], pmData?: SpreedlyPaymentMethodResponse): PaymentPayload<ItemSubscriptionPayload> {
        // TODO: move to @bciti/types
        interface ItemSubscriptionPayloadWithTargetUser extends ItemSubscriptionPayload {
            targetUser?: string;
        }

        const items: ItemSubscriptionPayloadWithTargetUser[] = [];

        subscriptions.forEach((sub) => {
            items.push({
                _id: this.orgSubscriptions.find((os) => os.code === sub.code)._id,
                amount: sub.cost,
                code: sub.code,
                targetUser: this.userId,
            });
        });

        return {
            transactionType: TransactionType.SUBSCRIPTION,
            reference: pmData?.token || this.inPersonForm.get('reference').value,
            paymentMethodType: this.isFree ? PaymentMethodType.COUNTER : this.selectedPaymentType.val,
            totalAmount: +this.itemsSubtotal.toFixed(2) + +this.taxesTotal.toFixed(2),
            totalTaxAmount: +this.taxesTotal.toFixed(2),
            items,
            retainCard: false,
        };
    }

    public closeDialog(val?: boolean) {
        this.dialogRef.close(val);
    }

    public submit() {
        this.processingSubmit = true;
        this.clearErrors();

        switch (this.selectedPaymentType.val) {
            case PaymentMethodType.CREDIT_CARD:
                if (this.isFree) {
                    this.postPayment();
                } else {
                    this.submitSpreedly();
                }
                break;
            case PaymentMethodType.COUNTER:
                this.submitInPerson();
                break;
            default:
                console.error('Incorrect payment type');
                break;
        }

        //TODO: Add step messages to display to user (calling spreedly..., confirming payment...)
    }

    get isFree() {
        return this.itemsSubtotal !== undefined && this.taxesTotal !== undefined && this.itemsSubtotal + this.taxesTotal === 0;
    }

    submitSpreedly() {
        this.Spreedly.validate();

        const tokenizePaymentPayload = {
            full_name: (document.getElementById('full_name') as HTMLInputElement).value,
            month: (document.getElementById('month') as HTMLInputElement).value,
            year: (document.getElementById('year') as HTMLInputElement).value,
        };

        this.Spreedly.tokenizeCreditCard(tokenizePaymentPayload);

        this.Spreedly.on('errors', (errors) => {
            errors.forEach((error) => console.error(error));
        });

        this.Spreedly.on('paymentMethod', (token: string, pmData: SpreedlyPaymentMethodResponse) => {
            this.postPayment(pmData);
        });
    }

    submitInPerson() {
        this.postPayment();
    }

    postPayment(pmData?: SpreedlyPaymentMethodResponse) {
        this.service
            .postPayment(this.preparePayload(this.data, pmData), this.userId)
            .pipe(take(1))
            .subscribe(
                (response) => {
                    this.messageService.success(this.translate.instant('pages.user-subscriptions.payment-dialog.messages.success'), undefined, { timeOut: 6000 });
                    this.closeDialog(true);
                },
                (error) => {
                    this.errors = error.error.messages;
                    this.showErrors = true;
                    this.processingSubmit = false;
                    // this.disableSubmit = true;
                },
            );
    }

    handleSelectPaymentType(e) {
        this.selectedPaymentType = e;
    }

    ngOnDestroy() {
        this.Spreedly.removeHandlers(); // We must unregister from eventHandlers in order to avoir duplicated api calls
        this.destroy$.next();
        this.destroy$.complete();
    }
}
